import { useCallback, useSyncExternalStore } from 'react';
import { VERSION } from '../config/version';

const NORMAL_INTERVAL = 15 * 60 * 1000; // 15 minutes
const MAINTENANCE_INTERVAL = 30 * 1000; // 30 seconds
const VERSION_URL = '/version.json';

interface MaintenanceData {
  enabled: boolean;
  title?: string;
  message?: string;
  estimatedEnd?: string;
}

interface VersionData {
  version: string;
  buildDate: string;
  stage: string;
  maintenance?: MaintenanceData;
}

interface MaintenanceState {
  enabled: boolean;
  title: string;
  message: string;
  estimatedEnd: string | null;
}

interface VersionState {
  updateAvailable: boolean;
  latestVersion: string | null;
  buildDate: string | null;
  dismissed: boolean;
  maintenance: MaintenanceState;
}

// Module-level singleton state — shared across all hook consumers
let state: VersionState = {
  updateAvailable: false,
  latestVersion: null,
  buildDate: null,
  dismissed: sessionStorage.getItem('updateDismissed') === 'true',
  maintenance: {
    enabled: false,
    title: '',
    message: '',
    estimatedEnd: null,
  },
};

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach(l => l());
}

function setState(partial: Partial<VersionState>) {
  state = { ...state, ...partial };
  notify();
}

// Debounce: skip if checked within the last 5 seconds
let lastCheckTime = 0;
const DEBOUNCE_MS = 5_000;

async function checkVersion() {
  if (import.meta.env.DEV) return;  // Skip version polling in local dev

  const now = Date.now();
  if (now - lastCheckTime < DEBOUNCE_MS) return;
  lastCheckTime = now;

  try {
    const response = await fetch(`${VERSION_URL}?t=${now}`);

    if (!response.ok) {
      console.warn('Version check failed: Unable to fetch version.json');
      scheduleNextCheck(state.maintenance.enabled);
      return;
    }

    const data: VersionData = await response.json();

    const currentVersion = VERSION.toString();
    const fetchedVersion = data.version;

    // Parse maintenance state
    const maintenance: MaintenanceState = data.maintenance?.enabled
      ? {
          enabled: true,
          title: data.maintenance.title || 'Scheduled Maintenance',
          message: data.maintenance.message || "We're performing scheduled maintenance. Please check back shortly.",
          estimatedEnd: data.maintenance.estimatedEnd || null,
        }
      : {
          enabled: false,
          title: '',
          message: '',
          estimatedEnd: null,
        };

    setState({
      latestVersion: fetchedVersion,
      buildDate: data.buildDate,
      maintenance,
    });

    // Schedule next check with adaptive interval
    scheduleNextCheck(maintenance.enabled);

    if (currentVersion !== fetchedVersion) {
      console.log(`Update available: current=${currentVersion}, latest=${fetchedVersion}`);
      setState({ updateAvailable: true, dismissed: false });
      sessionStorage.removeItem('updateDismissed');
    } else {
      console.log(`Version check: up to date (${currentVersion})`);
    }
  } catch (error) {
    console.error('Version check failed:', error);
    // On failure, keep polling at current rate
    scheduleNextCheck(state.maintenance.enabled);
  }
}

// Adaptive polling via setTimeout instead of fixed setInterval
let pollTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleNextCheck(maintenanceActive: boolean) {
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
  }
  const interval = maintenanceActive ? MAINTENANCE_INTERVAL : NORMAL_INTERVAL;
  pollTimer = setTimeout(checkVersion, interval);
}

// Single set of listeners + interval, initialized once
let initialized = false;
function initialize() {
  if (initialized) return;
  initialized = true;

  checkVersion();
  // Initial scheduling happens inside checkVersion after first fetch

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkVersion();
    }
  });

  window.addEventListener('focus', () => {
    checkVersion();
  });
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot() {
  return state;
}

export const useVersionCheck = () => {
  // Initialize on first hook usage (once ever)
  initialize();

  const current = useSyncExternalStore(subscribe, getSnapshot);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem('updateDismissed', 'true');
    setState({ dismissed: true });
  }, []);

  const handleUpdate = useCallback(() => {
    window.location.reload();
  }, []);

  const forceCheck = useCallback(() => {
    sessionStorage.removeItem('updateDismissed');
    setState({ dismissed: false });
    return checkVersion();
  }, []);

  return {
    updateAvailable: current.updateAvailable && !current.dismissed,
    onDismiss: handleDismiss,
    onUpdate: handleUpdate,
    forceCheck,
    latestVersion: current.latestVersion,
    buildDate: current.buildDate,
    currentVersion: VERSION.toString(),
    maintenance: current.maintenance,
  };
};
